import { useMemo } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useAuth } from '../hooks/useAuth';

const SupportPage = () => {
  const { store, isAuthenticated } = useAuth();
  const salesRep = store?.salesRep;

  // Format delivery days from route data
  const deliveryDays = useMemo(() => {
    if (!store?.route?.routeDays?.length) return [];

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    return store.route.routeDays
      .map(rd => {
        const dayId = rd.optDay?.id || rd.optDayID;
        if (dayId >= 1 && dayId <= 7) {
          return rd.optDay?.name || dayNames[dayId - 1] || dayNames[dayId];
        }
        return rd.optDay?.name;
      })
      .filter(Boolean);
  }, [store?.route?.routeDays]);

  return (
    <Container className="py-5">
      <div className="mx-auto" style={{ maxWidth: '900px' }}>
        <h1 className="text-center mb-4">Support</h1>

        <p className="text-center mb-5 lead">
          We're here to help. Whether you have questions about your order, product availability,
          or need assistance with your account, our team is ready to assist you.
        </p>

        <Row className="g-4 mb-5">
          <Col md={isAuthenticated && salesRep ? 4 : 6}>
            <Card className="h-100 text-center p-4">
              <Card.Body>
                <div className="mb-3">
                  <i className="bi bi-telephone fs-1 text-stash-accent"></i>
                </div>
                <h4>Call Us</h4>
                <p className="text-muted mb-3">
                  Speak directly with our support team during business hours.
                </p>
                <a href="tel:8662003155" className="btn btn-outline-stash-accent">
                  (866) 200-3155
                </a>
              </Card.Body>
            </Card>
          </Col>

          <Col md={isAuthenticated && salesRep ? 4 : 6}>
            <Card className="h-100 text-center p-4">
              <Card.Body>
                <div className="mb-3">
                  <i className="bi bi-envelope fs-1 text-stash-accent"></i>
                </div>
                <h4>Email Us</h4>
                <p className="text-muted mb-3">
                  Send us a message and we'll respond as soon as possible.
                </p>
                <a href="mailto:info@stashhousedistro.com" className="btn btn-outline-stash-accent">
                  info@stashhousedistro.com
                </a>
              </Card.Body>
            </Card>
          </Col>

          {/* Sales Rep card - only shown when logged in and has a sales rep */}
          {isAuthenticated && salesRep && (
            <Col md={4}>
              <Card className="h-100 text-center p-4">
                <Card.Body>
                  <div className="mb-3">
                    <i className="bi bi-person-badge fs-1 text-stash-accent"></i>
                  </div>
                  <h4>Your Sales Rep</h4>
                  <p className="text-muted mb-2">
                    {salesRep.name}
                  </p>
                  {salesRep.phone && (
                    <a href={`tel:${salesRep.phone}`} className="btn btn-outline-stash-accent mb-2 d-block">
                      {salesRep.phone}
                    </a>
                  )}
                  {salesRep.email && (
                    <a href={`mailto:${salesRep.email}`} className="btn btn-outline-stash-accent d-block">
                      {salesRep.email}
                    </a>
                  )}
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>

        {/* Generic sales rep card for non-logged in users */}
        {(!isAuthenticated || !salesRep) && (
          <Card className="p-4 mb-5">
            <Card.Body className="text-center">
              <h4 className="mb-3">Contact Your Sales Representative</h4>
              <p>
                For personalized assistance with orders, product recommendations, pricing inquiries,
                or account-specific questions, we recommend reaching out to your dedicated sales representative.
                They're familiar with your business needs and can provide the fastest, most tailored support.
              </p>
              <p className="text-muted mb-0">
                Not sure who your sales rep is? Give us a call or send an email and we'll connect you.
              </p>
            </Card.Body>
          </Card>
        )}

        <h3 className="mb-4">Frequently Asked Questions</h3>

        <div className="mb-4">
          <h5>How do I place an order?</h5>
          <p>
            Browse our catalog, add items to your cart, and proceed to checkout. If you need help
            finding specific products or have questions about quantities, contact your sales rep.
          </p>
        </div>

        <div className="mb-4">
          <h5>What are your delivery areas?</h5>
          <p>
            We deliver to licensed dispensaries throughout our service area. Contact us to confirm
            delivery availability for your location.
          </p>
        </div>

        <div className="mb-4">
          <h5>How do I check on my order status?</h5>
          <p>
            You can view your order history and status in your account dashboard. For urgent inquiries
            about an existing order, please call us directly.
          </p>
        </div>

        <div className="mb-4">
          <h5>I'm having trouble with my account. What should I do?</h5>
          <p>
            For login issues, password resets, or account access problems, email us at{' '}
            <a href="mailto:info@stashhousedistro.com">info@stashhousedistro.com</a> or call our
            support line and we'll get you sorted out.
          </p>
        </div>

        <div className="mb-4">
          <h5>What are my delivery days and order cutoffs?</h5>
          <p>
            Each dispensary is assigned specific delivery days based on your location and route.
            {isAuthenticated && deliveryDays.length > 0 && (
              <>
                {' '}Your scheduled delivery days are: <strong className="stash-text-accent">{deliveryDays.join(', ')}</strong>.
              </>
            )}
          </p>
          <p>
            Please allow at least 24 hours for order processing. To ensure your order arrives on your
            scheduled delivery day, orders must be placed before the cutoff time. You can view the countdown
            to your next order cutoff in your cart. If you need to change your delivery schedule, contact
            your sales rep.
          </p>
        </div>

        <div className="mb-4">
          <h5>How do I become a customer?</h5>
          <p>
            Stash House Distribution serves licensed cannabis dispensaries. To set up a wholesale account,
            contact us with your dispensary license information and we'll guide you through the onboarding process.
          </p>
        </div>
      </div>
    </Container>
  );
};

export default SupportPage;